import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import methodModel from "../../methods/methods";
import {
  getAllCartItems,
  openDB,
  removeCartItem,
  setCartItem,
  clearAllCartItems,
} from "../Products/ProductDetail";
import environment from "../../environment";
import { BsCart3 } from "react-icons/bs";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

// ─── Quantity Selector ────────────────────────────────────────────────────────

function QuantitySelector({
  quantity,
  onDecrease,
  onIncrease,
  disabled,
  size = "md",
}) {
  const isSmall = size === "sm";
  return (
    <div
      className={`flex items-center w-fit bg-[#F8F8F8] border border-[#C3C3C3] rounded-lg overflow-hidden transition-opacity duration-150 ${disabled ? "opacity-50 pointer-events-none" : ""
        }`}
    >
      <button
        type="button"
        disabled={disabled || quantity <= 1}
        onClick={onDecrease}
        className={`${isSmall ? "w-[28px] h-[26px]" : "w-[38px] h-[34px]"} flex items-center justify-center text-[#116DAE] font-bold
          hover:bg-[#EBF3FB] active:bg-[#D6E9F8] active:scale-90 transition-all
          disabled:opacity-30 disabled:cursor-not-allowed select-none`}
      >
        −
      </button>
      <span
        className={`${isSmall ? "w-[28px] text-xs" : "w-[38px] text-sm"} text-center font-semibold text-[#1a1a1a] select-none tabular-nums`}
      >
        {quantity}
      </span>
      <button
        type="button"
        disabled={disabled}
        onClick={onIncrease}
        className={`${isSmall ? "w-[28px] h-[26px]" : "w-[38px] h-[34px]"} flex items-center justify-center text-[#116DAE] font-bold
          hover:bg-[#EBF3FB] active:bg-[#D6E9F8] active:scale-90 transition-all
          disabled:opacity-30 disabled:cursor-not-allowed select-none`}
      >
        +
      </button>
    </div>
  );
}

// ─── Remove Confirm Toast ─────────────────────────────────────────────────────

function RemoveToast({ show, onHide }) {
  useEffect(() => {
    if (show) {
      const t = setTimeout(onHide, 2500);
      return () => clearTimeout(t);
    }
  }, [show, onHide]);

  if (!show) return null;

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[10001]
        flex items-center gap-3 bg-white border border-[#fecaca]
        rounded-2xl shadow-2xl px-5 py-3
        transition-all duration-300 ease-out opacity-100 translate-y-0 scale-100"
    >
      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path
            d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"
            stroke="#ef4444"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <p className="text-sm font-semibold text-[#1a1a1a] whitespace-nowrap">
        Item removed from cart.
      </p>
      <button
        onClick={onHide}
        className="ml-1 text-gray-300 hover:text-gray-500 transition-colors"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path
            d="M18 6L6 18M6 6l12 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}

// ─── Order Success Modal ──────────────────────────────────────────────────────

function OrderSuccessModal({ show, onClose, navigate }) {
  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="quote-success-title"
      className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4
        transition-all duration-300 opacity-100 pointer-events-auto"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full text-center
          transition-all duration-300 ease-out opacity-100 scale-100 translate-y-0"
      >
        <div className="w-[180px] rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
          <img
            src="/assets/img/success.gif"
            className="w-full h-full object-contain"
            alt="Success"
          />
        </div>
        <h3 id="quote-success-title" className="text-xl font-bold text-[#1a1a1a] mb-2">
          Quote Requested!
        </h3>
        <p className="text-sm text-gray-500 mb-6 leading-relaxed">
          Your quote request has been submitted successfully.
          <br />
          You can track its status anytime from{" "}
          <span className="font-medium text-[#115B9D]">My Documents</span>. We'll
          notify you once the supplier responds.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={(e) => {
              onClose();
              navigate("/orders");
            }}
            className="flex-1 bg-gradient-to-r from-[#115B9D] to-[#116DAE]
      text-white font-semibold rounded-xl py-3 px-6
      shadow-md hover:shadow-lg hover:-translate-y-0.5
      transition-all duration-300"
          >
            View Details
          </button>

          <button
            onClick={onClose}
            className="flex-1 bg-white border border-[#115B9D]
      text-[#115B9D] font-semibold rounded-xl py-3 px-6
      hover:bg-blue-50 hover:border-[#116DAE]
      transition-all duration-300"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Guest/Signup Choice Modal ─────────────────────────────────────────────────

function GuestOrSignupModal({ show, onClose, onSelectGuest, onSelectSignup }) {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[10003] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full transition-all duration-300 ease-out">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-[#EBF3FB] flex items-center justify-center mx-auto mb-4">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#116DAE"
              strokeWidth="1.8"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-[#1a1a1a] mb-2">
            Request a Quote
          </h3>
          <p className="text-sm text-gray-500">
            Would you like to continue as a guest or sign up for an account?
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={onSelectGuest}
            className="w-full py-3 px-4 bg-white border-2 border-[#116DAE] text-[#116DAE] font-semibold rounded-xl
              hover:bg-[#EBF3FB] transition-all duration-200 flex items-center justify-center gap-2"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            Continue as Guest
          </button>

          <button
            onClick={onSelectSignup}
            className="w-full py-3 px-4 bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white font-semibold rounded-xl
              hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            Sign Up / Log In
          </button>
        </div>

        <button
          onClick={onClose}
          className="w-full mt-4 py-2 text-gray-400 text-sm hover:text-gray-600 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Quote Modal ──────────────────────────────────────────────────────────────

function QuoteModal({ show, onClose, onSubmit, cartItems, detail }) {
  const user = useSelector((state) => state.user);
  const [sameAsBilling, setSameAsBilling] = useState(false);

  const [form, setForm] = useState({
    customer_name: user?.fullName || "",
    email: user?.email || "",
    billingAddress: {
      bill_line1: "",
      bill_line2: "",
      bill_city: "",
      bill_country: "",
      bill_postal_code: "",
    },
    shippingAddress: {
      ship_line1: "",
      ship_line2: "",
      ship_city: "",
      ship_country: "",
      ship_postal_code: "",
    },
  });

  // Update form when detail or user changes
  useEffect(() => {
    if (show) {
      setForm({
        customer_name: user?.fullName || detail?.customer_name || "",
        email: user?.email || detail?.email || "",
        billingAddress: {
          bill_line1: detail?.billingAddress?.bill_line1 || "",
          bill_line2: detail?.billingAddress?.bill_line2 || "",
          bill_city: detail?.billingAddress?.bill_city || "",
          bill_country: detail?.billingAddress?.bill_country || "",
          bill_postal_code: detail?.billingAddress?.bill_postal_code || "",
        },
        shippingAddress: {
          ship_line1: detail?.shippingAddress?.ship_line1 || "",
          ship_line2: detail?.shippingAddress?.ship_line2 || "",
          ship_city: detail?.shippingAddress?.ship_city || "",
          ship_country: detail?.shippingAddress?.ship_country || "",
          ship_postal_code: detail?.shippingAddress?.ship_postal_code || "",
        },
      });

      // Reset checkbox when modal opens with new detail
      setSameAsBilling(false);
    }
  }, [show, detail, user]); // Re-run when show, detail, or user changes

  const handleChange = (e, section, field) => {
    const value = e.target.value;
    if (section) {
      setForm((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  useEffect(() => {
    if (sameAsBilling) {
      setForm((prev) => ({
        ...prev,
        shippingAddress: {
          ship_line1: prev.billingAddress.bill_line1,
          ship_line2: prev.billingAddress.bill_line2,
          ship_city: prev.billingAddress.bill_city,
          ship_country: prev.billingAddress.bill_country,
          ship_postal_code: prev.billingAddress.bill_postal_code,
        },
      }));
    }
  }, [sameAsBilling, form.billingAddress]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 overflow-y-auto py-10">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full my-auto transition-all duration-300 ease-out">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-[#1a1a1a]">
            Request Quote Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Name *
              </label>
              <input
                required
                type="text"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#116DAE] outline-none capitalize"
                value={form.customer_name}
                onChange={(e) => handleChange(e, null, "customer_name")}
                disabled={!!user?._id}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                required
                type="email"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#116DAE] outline-none"
                value={form.email}
                onChange={(e) => handleChange(e, null, "email")}
                disabled={!!user?._id}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Billing Address */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900 border-b pb-2">
                Billing Address
              </h4>
              <input
                required
                type="text"
                placeholder="Address Line 1 *"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none"
                value={form.billingAddress.bill_line1}
                onChange={(e) =>
                  handleChange(e, "billingAddress", "bill_line1")
                }
              />
              <input
                type="text"
                placeholder="Address Line 2"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none"
                value={form.billingAddress.bill_line2}
                onChange={(e) =>
                  handleChange(e, "billingAddress", "bill_line2")
                }
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  required
                  type="text"
                  placeholder="City *"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none"
                  value={form.billingAddress.bill_city}
                  onChange={(e) =>
                    handleChange(e, "billingAddress", "bill_city")
                  }
                />
                <input
                  required
                  type="text"
                  placeholder="Postal Code *"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none"
                  value={form.billingAddress.bill_postal_code}
                  onChange={(e) =>
                    handleChange(e, "billingAddress", "bill_postal_code")
                  }
                />
              </div>
              <input
                required
                type="text"
                placeholder="Country *"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none"
                value={form.billingAddress.bill_country}
                onChange={(e) =>
                  handleChange(e, "billingAddress", "bill_country")
                }
              />
            </div>

            {/* Shipping Address */}
            <div className="space-y-3">
              <div className="flex items-center justify-between border-b pb-2">
                <h4 className="font-semibold text-gray-900">
                  Shipping Address
                </h4>
                <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sameAsBilling}
                    onChange={(e) => setSameAsBilling(e.target.checked)}
                    className="rounded text-[#116DAE]"
                  />
                  Same as Billing
                </label>
              </div>
              <input
                required
                type="text"
                disabled={sameAsBilling}
                placeholder="Address Line 1 *"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none disabled:bg-gray-50"
                value={form.shippingAddress.ship_line1}
                onChange={(e) =>
                  handleChange(e, "shippingAddress", "ship_line1")
                }
              />
              <input
                disabled={sameAsBilling}
                type="text"
                placeholder="Address Line 2"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none disabled:bg-gray-50"
                value={form.shippingAddress.ship_line2}
                onChange={(e) =>
                  handleChange(e, "shippingAddress", "ship_line2")
                }
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  required
                  disabled={sameAsBilling}
                  type="text"
                  placeholder="City *"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none disabled:bg-gray-50"
                  value={form.shippingAddress.ship_city}
                  onChange={(e) =>
                    handleChange(e, "shippingAddress", "ship_city")
                  }
                />
                <input
                  required
                  disabled={sameAsBilling}
                  type="text"
                  placeholder="Postal Code *"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none disabled:bg-gray-50"
                  value={form.shippingAddress.ship_postal_code}
                  onChange={(e) =>
                    handleChange(e, "shippingAddress", "ship_postal_code")
                  }
                />
              </div>
              <input
                required
                disabled={sameAsBilling}
                type="text"
                placeholder="Country *"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none disabled:bg-gray-50"
                value={form.shippingAddress.ship_country}
                onChange={(e) =>
                  handleChange(e, "shippingAddress", "ship_country")
                }
              />
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-200 text-gray-600 font-semibold rounded-full hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white font-semibold rounded-full shadow hover:scale-[1.02] active:scale-[0.98] transition"
            >
              Submit Quote Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Drawer Cart Item ─────────────────────────────────────────────────────────

function DrawerCartItem({ item, onQuantityChange, onRemove, updating }) {
  const [removing, setRemoving] = useState(false);

  const handleRemove = async (id) => {
    setRemoving(true);
    await onRemove(id);
  };

  return (
    <div
      className={`flex gap-3 border border-gray-100 rounded-xl p-3 bg-white
        hover:shadow-sm transition-all duration-200
        ${removing ? "opacity-0 scale-95 -translate-x-2" : "opacity-100 scale-100 translate-x-0"}`}
    >
      <div
        className="w-[64px] h-[64px] rounded-xl bg-cover bg-no-repeat flex-shrink-0 overflow-hidden"
        style={{ backgroundImage: "url('/assets/cards-background.png')" }}
      >
        {item.image ? (
          <img
            src={methodModel.userImg(item.image)}
            className="w-full h-full object-contain p-1"
            alt={item.productName}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[#EBF3FB] rounded-xl">
            <svg width="24" height="24" fill="none" viewBox="0 0 64 64">
              <rect
                x="8"
                y="20"
                width="48"
                height="36"
                rx="5"
                fill="#EBF3FB"
                stroke="#C8DCF0"
                strokeWidth="2"
              />
              <path
                d="M20 20V16a12 12 0 0124 0v4"
                stroke="#C8DCF0"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle
                cx="32"
                cy="36"
                r="6"
                fill="#D6E9F8"
                stroke="#116DAE"
                strokeWidth="1.5"
              />
            </svg>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5 justify-between w-full min-w-0">
        <div>
          <p className="text-[11px] text-[#414141] truncate">
            {item.manufacturerId || item.manufacturerId || "—"}
          </p>
          <p className="capitalize text-xs font-medium line-clamp-2 text-[#1a1a1a] leading-relaxed">
            {item.productName}
          </p>
          {item.model && (
            <p className="text-[11px] text-gray-400 mt-0.5">
              Model: {item.model}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          <QuantitySelector
            size="sm"
            quantity={item.quantity}
            disabled={updating}
            onDecrease={() => onQuantityChange(item.id, item.quantity - 1)}
            onIncrease={() => onQuantityChange(item.id, item.quantity + 1)}
          />
          <button
            onClick={() => handleRemove(item.id)}
            disabled={updating}
            className="text-[11px] border border-[#116AAB] text-[#116AAB] rounded-full
              hover:bg-[#116AAB] hover:text-white active:scale-95
              transition-all duration-200 py-1 px-3
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Cart Drawer ──────────────────────────────────────────────────────────────

function CartDrawer({
  open,
  onClose,
  cartItems,
  onQuantityChange,
  onRemove,
  updating,
  onRequestQuote,
  placingOrder,
}) {
  const navigate = useNavigate();

  const totalItems = cartItems.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = cartItems.reduce(
    (sum, i) => sum + (i.price || 0) * i.quantity,
    0,
  );
  const taxes = +(subtotal * 0.1).toFixed(2);
  const total = +(subtotal + taxes).toFixed(2);
  const formatPrice = (val) => (val > 0 ? `$${val.toFixed(2)}` : null);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const handler = (e) => e.key === "Escape" && open && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-[9998] bg-black/40 backdrop-blur-[2px]
          transition-all duration-300
          ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
        aria-hidden={!open}
        className={`fixed top-0 right-0 z-[9999999] h-full w-full max-w-[400px] bg-white
          flex flex-col shadow-2xl
          transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]
          ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-white z-10">
          <div className="flex items-center gap-2.5">
            <BsCart3 className="text-[#116DAE] text-xl" />
            <h2 className="text-base font-semibold text-[#1a1a1a]">
              Your Cart
            </h2>
            {totalItems > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1
                rounded-full bg-[#EBF3FB] text-[#116DAE] text-[11px] font-bold transition-all duration-300"
              >
                {totalItems}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close cart"
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50
              hover:bg-gray-100 active:scale-90 transition-all text-gray-400 hover:text-gray-600"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto scrollbar px-4 py-4 space-y-3 overscroll-contain">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <div className="w-16 h-16 rounded-full bg-[#EBF3FB] flex items-center justify-center mb-4 animate-[pulse_2s_ease-in-out_infinite]">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#116DAE"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-[#1a1a1a]">
                Your cart is empty
              </p>
              <p className="text-xs text-gray-400 mt-1 mb-5">
                Add items to get started.
              </p>
              <button
                onClick={() => {
                  onClose();
                  navigate("/products");
                }}
                className="bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white font-semibold
                  px-6 rounded-full shadow hover:scale-[1.04] active:scale-[0.98] duration-300 transition py-2 text-sm"
              >
                Browse Products
              </button>
            </div>
          ) : (
            cartItems.map((item) => (
              <DrawerCartItem
                key={item.id}
                item={item}
                onQuantityChange={onQuantityChange}
                onRemove={onRemove}
                updating={updating}
              />
            ))
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="px-5 py-4 border-t border-gray-100 space-y-3 bg-white shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
            {/* <div className="rounded-xl border border-gray-100 p-4 space-y-2 bg-[#FAFAFA]">
              <div className="flex justify-between text-sm text-[#4E4E4E]">
                <span>Subtotal</span>
                <span className="font-medium text-[#1a1a1a]">
                  {formatPrice(subtotal) ?? <span className="text-gray-400 text-xs italic">—</span>}
                </span>
              </div>
              <div className="flex justify-between text-sm text-[#4E4E4E]">
                <span>Shipping</span>
                <span className="font-medium text-[#42A660]">Free</span>
              </div>
              <div className="flex justify-between text-sm text-[#4E4E4E]">
                <span>Taxes (10%)</span>
                <span className="font-medium text-[#1a1a1a]">
                  {formatPrice(taxes) ?? <span className="text-gray-400 text-xs italic">—</span>}
                </span>
              </div>
              <div className="flex justify-between text-sm font-semibold text-[#1a1a1a] pt-2 border-t border-gray-200">
                <span>Total</span>
                <span>
                  {formatPrice(total) ?? <span className="text-gray-400 font-normal text-xs italic">—</span>}
                </span>
              </div>
              <p className="text-xs text-gray-400 text-right">
                {totalItems} item{totalItems !== 1 ? "s" : ""} in cart
              </p>
            </div> */}

            <button
              onClick={onRequestQuote}
              disabled={placingOrder}
              className="w-full bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white font-semibold
                rounded-full shadow hover:scale-[1.03] active:scale-[0.98] duration-300 transition py-2.5 text-sm
                disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100
                flex items-center justify-center gap-2"
            >
              {placingOrder ? (
                <>
                  <svg
                    className="animate-spin w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="white"
                      strokeWidth="3"
                    />
                    <path
                      className="opacity-75"
                      fill="white"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  Requesting Quote…
                </>
              ) : (
                "Request Quote"
              )}
            </button>

            <button
              onClick={() => {
                onClose();
                navigate("/products");
              }}
              className="w-full border border-[#116AAB] text-[#116AAB] font-medium rounded-full
                hover:bg-[#EBF3FB] hover:scale-[1.03] active:scale-[0.98] duration-300 transition py-2.5 text-sm"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Floating Cart Button ─────────────────────────────────────────────────────

function FloatingCartButton({ totalItems, onClick, visible }) {
  const prevCount = useRef(totalItems);
  const [bump, setBump] = useState(false);

  useEffect(() => {
    if (totalItems !== prevCount.current) {
      setBump(true);
      const t = setTimeout(() => setBump(false), 400);
      prevCount.current = totalItems;
      return () => clearTimeout(t);
    }
  }, [totalItems]);

  return (
    <button
      onClick={onClick}
      aria-label={`Open cart${totalItems > 0 ? `, ${totalItems} item${totalItems !== 1 ? "s" : ""}` : ""}`}
      className={`fixed bottom-[4.5rem] right-[8px] z-[9990] w-12 h-12 rounded-full
        bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white
        flex items-center justify-center
        shadow-[0_4px_20px_#115d9e73)]
        hover:scale-110 active:scale-95
        transition-all duration-300 ease-out
        ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8 pointer-events-none"}
        ${bump ? "scale-125" : ""}`}
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0" />
      </svg>
      {totalItems > 0 && (
        <span
          className={`absolute -top-[8px] -right-0 min-w-[20px] h-5 px-1
            bg-red-500 text-white text-[10px] font-bold rounded-full
            flex items-center justify-center border-2 border-white
            transition-transform duration-300
            ${bump ? "scale-125" : "scale-100"}`}
        >
          {totalItems > 99 ? "99+" : totalItems}
        </span>
      )}
    </button>
  );
}

// ─── Main FloatingCart Component ──────────────────────────────────────────────

export default function FloatingCart() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showRemoveToast, setShowRemoveToast] = useState(false);
  const [showOrderSuccess, setShowOrderSuccess] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showGuestSignupModal, setShowGuestSignupModal] = useState(false);
  const [detail, setDetail] = useState(null);

  // Add a state to track if user is logged in
  // Replace this with your actual auth check logic
  const [isLoggedIn, setIsLoggedIn] = useState(user?._id ? true : false);

  // Check login status on mount (implement according to your auth system)
  // useEffect(() => {
  //   // Example: Check if user token exists in localStorage or context
  //   const token = localStorage.getItem("token");
  //   setIsLoggedIn(!!token);
  // }, []);

  const loadCart = useCallback(async () => {
    try {
      const items = await getAllCartItems();
      setCartItems(items || []);
    } catch (err) {
      console.error("Failed to load cart:", err);
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  // Sync badge when any page adds/removes from cart
  useEffect(() => {
    const handler = () => loadCart();
    window.addEventListener("cart-updated", handler);
    return () => window.removeEventListener("cart-updated", handler);
  }, [loadCart]);

  // ── Open drawer from anywhere via: window.dispatchEvent(new Event("open-cart-drawer")) ──
  useEffect(() => {
    const handler = () => setDrawerOpen(true);
    window.addEventListener("open-cart-drawer", handler);
    return () => window.removeEventListener("open-cart-drawer", handler);
  }, []);

  const handleQuantityChange = useCallback(
    async (id, newQty) => {
      if (newQty < 1) return;
      setUpdating(true);
      try {
        const item = cartItems.find((i) => i.id === id);
        if (!item) return;
        const updated = { ...item, quantity: newQty };
        await setCartItem(updated);
      } catch (err) {
        console.error("Update qty error:", err);
      } finally {
        setUpdating(false);
      }
    },
    [cartItems],
  );

  const handleRemove = useCallback(async (id) => {
    setUpdating(true);
    try {
      await removeCartItem(id);
      setShowRemoveToast(true);
    } catch (err) {
      console.error("Remove error:", err);
    } finally {
      setUpdating(false);
    }
  }, []);

  const [showQuoteModal, setShowQuoteModal] = useState(false);

  const handleRequestQuote = useCallback(() => {
    if (cartItems.length === 0) return;

    // If user is not logged in, show guest/signup modal first
    if (!isLoggedIn) {
      setDrawerOpen(false);
      setShowGuestSignupModal(true);
    } else {
      // User is logged in, directly show quote modal
      setDrawerOpen(false);
      setShowQuoteModal(true);
    }
  }, [cartItems, isLoggedIn]);

  // ── Auto-open quote modal via event from ProductDetail ──
  useEffect(() => {
    const handler = () => {
      handleRequestQuote();
    };
    window.addEventListener("auto-request-quote", handler);
    return () => window.removeEventListener("auto-request-quote", handler);
  }, [handleRequestQuote]);

  const handleGuestSelection = () => {
    // User chose to continue as guest, show quote modal
    setShowGuestSignupModal(false);
    setShowQuoteModal(true);
  };

  const handleSignupSelection = () => {
    // User chose to sign up, redirect to signup page
    setShowGuestSignupModal(false);
    // Store cart items or intent in localStorage to redirect back after signup
    localStorage.setItem("redirectAfterSignup", "/products");
    localStorage.setItem("showQuoteAfterSignup", "true");
    navigate("/signup"); // Adjust this to your signup route
  };

  const submitQuote = async (formData) => {
    setPlacingOrder(true);
    loader(true);
    try {
      const payload = {
        ...formData,
        line_items: cartItems.map((item) => ({
          item_name: item.productName,
          description: item.productName || "",
          qty: item.quantity,
          rate: null,
          amount: null,
        })),
      };

      const res = await ApiClient.post("request-quote/add", payload);

      if (
        res &&
        (res.success === true ||
          res.status === "success" ||
          res.success !== false)
      ) {
        try {
          await clearAllCartItems();
        } catch (clearError) {
          console.error("Failed to clear cart:", clearError);
        }

        setShowQuoteModal(false);
        setShowOrderSuccess(true);
      } else {
        const errorMsg =
          res?.message || res?.error || "Failed to request quote";
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error("Quote request error:", err);
      if (err.response) {
        console.error("Error response data:", err.response.data);
        console.error("Error status:", err.response.status);
        toast.error(
          err.response.data?.message ||
          "An error occurred while requesting quote",
        );
      } else if (err.message && err.message.includes("memory fallback")) {
        // Specific handling for IndexedDB fallback - still consider it a success
        toast.success("Quote request submitted successfully!");
        try {
          await clearAllCartItems();
        } catch (clearError) {
          console.error("Failed to clear cart:", clearError);
        }
        setShowQuoteModal(false);
        setShowOrderSuccess(true);
      } else {
        toast.error("An error occurred while requesting quote");
      }
    } finally {
      setPlacingOrder(false);
      loader(false);
    }
  };

  const GetUserDetail = () => {
    if (!user?._id) return;
    ApiClient.get(`user/profile?id=${user?._id || user?.id}`).then((res) => {
      if (res?.success) {
        setDetail(res?.user_data);
      }
    });
  };

  const totalItems = cartItems.reduce((sum, i) => sum + i.quantity, 0);

  // Check if user should see quote modal after signup redirect or auto-open
  useEffect(() => {
    GetUserDetail();
    const showQuoteAfterSignup = localStorage.getItem("showQuoteAfterSignup");
    const autoOpenQuote = localStorage.getItem("autoOpenQuote");

    if (showQuoteAfterSignup === "true" && isLoggedIn) {
      localStorage.removeItem("showQuoteAfterSignup");
      setTimeout(() => {
        setShowQuoteModal(true);
      }, 500);
    } else if (autoOpenQuote === "true") {
      localStorage.removeItem("autoOpenQuote");
      setTimeout(() => {
        handleRequestQuote();
      }, 500);
    }
  }, [isLoggedIn, handleRequestQuote]);

  return (
    <>
      <FloatingCartButton
        totalItems={totalItems}
        onClick={() => setDrawerOpen(true)}
        visible={!loading}
      />

      <CartDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        cartItems={cartItems}
        onQuantityChange={handleQuantityChange}
        onRemove={handleRemove}
        updating={updating}
        onRequestQuote={handleRequestQuote}
        placingOrder={placingOrder}
      />

      {showGuestSignupModal && (
        <GuestOrSignupModal
          show={showGuestSignupModal}
          onClose={() => setShowGuestSignupModal(false)}
          onSelectGuest={handleGuestSelection}
          onSelectSignup={handleSignupSelection}
        />
      )}

      {showQuoteModal && (
        <QuoteModal
          show={showQuoteModal}
          onClose={() => setShowQuoteModal(false)}
          onSubmit={submitQuote}
          cartItems={cartItems}
          detail={detail}
        />
      )}

      {showRemoveToast && (
        <RemoveToast
          show={showRemoveToast}
          onHide={() => setShowRemoveToast(false)}
        />
      )}
      {showOrderSuccess && (
        <OrderSuccessModal
          show={showOrderSuccess}
          navigate={navigate}
          onClose={() => {
            setShowOrderSuccess(false);
            navigate("/products");
          }}
        />
      )}
    </>
  );
}
